/*
 * Copyright (C) Zoomdata, Inc. 2012-2022. All rights reserved.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { EmbeddedComponent } from './EmbeddedComponent';
import { ComponentTypes, InventoryEventNames } from './types';
export const InventoryItemTypes = {
    DASHBOARD: 'dashboard',
    VISUAL: 'visual',
    SOURCE: 'source',
};
export const DefaultIds = {
    DASHBOARD: {
        actions: ['export-selected-items', 'import-dashboard', 'add-new-dashboard'],
        filters: ['all', 'favorites', 'myitems', 'shared'],
        columns: [
            'selection',
            'favorite',
            'name',
            'description',
            'tags',
            'dataSource',
            'author',
            'dateModified',
            'share',
            'permissions',
            'schedule',
            'actions',
        ],
    },
    VISUAL: {
        actions: ['export-selected-items', 'import-visual', 'add-new-visual'],
        filters: ['all', 'favorites', 'myitems', 'shared'],
        columns: [
            'selection',
            'favorite',
            'type',
            'name',
            'description',
            'tags',
            'dataSource',
            'author',
            'dateModified',
            'permissions',
            'actions',
        ],
    },
    SOURCE: {
        actions: ['export-selected-items', 'import-source', 'add-new-source'],
        filters: ['all', 'favorites', 'myitems', 'shared'],
        columns: [
            'selection',
            'favorite',
            'type',
            'name',
            'description',
            'tags',
            'connection',
            'author',
            'dateModified',
            'permissions',
            'rowSecurity',
            'columnSecurity',
            'actions',
        ],
    },
};
export class EmbeddedInventory extends EmbeddedComponent {
    notificationSettings;
    inventoryItem;
    components;
    interactivityOverrides;
    componentType = ComponentTypes.INVENTORY;
    /**
     *
     * @param theme - The theme for the embedded inventory. Valid values are `composer`, `modern`, or `dark`.
     * The initial default theme, `composer`, is the same as the `modern` theme.
     * However, if you add your own themes to the application, more options are available in this list and you may have introduced a different default.
     * @param renderer
     * @param inventoryItem - Indicates type of entities shown in the embedded inventory. Each type has its own default settings for table columns, actions, etc.
     * @param components - Defines configuration of actions, search, filters, table columns, etc.
     * @param interactivityOverrides - Defines interactivity settings for an embedded inventory.
     * @param notificationSettings - Specifies settings for notifications
     */
    constructor(theme, renderer, inventoryItem, components, interactivityOverrides, notificationSettings) {
        super(theme, null, renderer, notificationSettings);
        this.notificationSettings = notificationSettings;
        const { actions = {}, filters = {}, search = {}, searchByField = {}, table = {} } = components || {};
        const isValidType = Object.values(InventoryItemTypes).includes(inventoryItem);
        if (!isValidType) {
            console.warn(`"${inventoryItem}" inventory item is not supported`);
        }
        this.inventoryItem = isValidType ? inventoryItem : InventoryItemTypes.DASHBOARD;
        this.components = {
            actions: {
                visible: true,
                ...actions,
            },
            filters: {
                visible: true,
                ...filters,
            },
            search: {
                visible: true,
                ...search,
            },
            searchByField: {
                visible: true,
                ...searchByField,
            },
            table: {
                defaultSort: {},
                ...table,
            },
        };
        this.interactivityOverrides = interactivityOverrides ?? {};
    }
    getApplicationParams() {
        const base = super.getApplicationParams();
        const itemType = this.inventoryItem;
        const interactivitySettings = this.interactivityOverrides.settings ?? {};
        return {
            ...base,
            componentProps: {
                ...this.components,
                itemType,
                interactivitySettings,
            },
        };
    }
    render(element, attributes) {
        super.render(element, attributes);
        this.htmlElement.addEventListener(InventoryEventNames.READY, this.removeLoader);
    }
    destroy() {
        this.htmlElement.removeEventListener(InventoryEventNames.READY, this.removeLoader);
    }
    addEventListener(eventName, callback) {
        this.htmlElement.addEventListener(eventName, callback);
    }
    removeEventListener(eventName, callback) {
        this.htmlElement.removeEventListener(eventName, callback);
    }
}
//# sourceMappingURL=EmbeddedInventory.js.map